import React from "react";
import { motion } from "framer-motion";
import {
  ShieldPlus, IdentificationCard, FileText, ClipboardText, Buildings,
  Question, Lightning, Check, ChatCircleDots, PaperPlaneRight,
} from "@phosphor-icons/react";

const FEATURES = [
  { icon: IdentificationCard, title: "Digital E-Card", desc: "Instant member health card, always in pocket", side: "left" },
  { icon: FileText, title: "Coverage Check", desc: "Ask what's covered, in plain language", side: "left" },
  { icon: ClipboardText, title: "File & Track Claims", desc: "Submit and follow claims via chat", side: "left" },
  { icon: Buildings, title: "Hospital Finder", desc: "Nearest cashless network hospitals", side: "right" },
  { icon: Question, title: "Instant FAQs", desc: "Answers to common insurance queries", side: "right" },
  { icon: Lightning, title: "24×7 AI Support", desc: "Always-on, no waiting on hold", side: "right" },
];

const STEPS = [
  { n: "1", t: "Say hi", d: "Open the chat and tap a quick action or type a question." },
  { n: "2", t: "Ask anything", d: "Coverage, e-card, claims or hospitals — the AI understands." },
  { n: "3", t: "Get rich replies", d: "See cards, e-cards and claim forms right in the conversation." },
  { n: "4", t: "Done in seconds", d: "No PDFs, no calls, no HR tickets. Just answers." },
];

function FeaturePill({ f, i, align }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: align === "right" ? 30 : -30 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: i * 0.08 }}
      data-testid={`infographic-feature-${i}`}
      className={`flex items-center gap-3 rounded-2xl border border-zinc-200 bg-white p-4 shadow-[0_2px_10px_rgba(0,0,0,0.03)] hover:-translate-y-0.5 hover:shadow-md transition-[transform,box-shadow] ${align === "right" ? "flex-row-reverse text-right" : ""}`}
    >
      <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
        <f.icon size={22} weight="duotone" className="text-emerald-700" />
      </div>
      <div>
        <p className="font-heading font-semibold text-emerald-950 text-sm">{f.title}</p>
        <p className="text-xs text-zinc-500 leading-snug">{f.desc}</p>
      </div>
    </motion.div>
  );
}

function PhoneMock() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="mx-auto w-[280px] shrink-0"
    >
      <div className="rounded-[2.5rem] border-[10px] border-emerald-950 bg-emerald-950 shadow-[0_25px_60px_rgba(0,0,0,0.25)] overflow-hidden">
        {/* header */}
        <div className="bg-emerald-900 px-4 py-3 flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-full bg-emerald-500 flex items-center justify-center relative">
            <ShieldPlus size={18} weight="fill" className="text-emerald-950" />
            <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-emerald-900" />
          </div>
          <div>
            <p className="text-white text-sm font-semibold leading-tight">Aarogya Assist Chatbot</p>
            <p className="text-[10px] text-emerald-300">online</p>
          </div>
        </div>
        {/* chat body */}
        <div className="chat-bg px-3 py-4 space-y-2.5 h-[360px]">
          <div className="flex justify-end">
            <div className="bg-emerald-900 text-white text-xs px-3 py-2 rounded-2xl rounded-br-sm max-w-[80%]">
              Show my e-health card
            </div>
          </div>
          <div className="flex justify-start">
            <div className="bg-white text-zinc-800 text-xs px-3 py-2 rounded-2xl rounded-bl-sm max-w-[80%] shadow-sm">
              Here's your digital card, Rahul 👇
            </div>
          </div>
          {/* mini e-card */}
          <div className="flex justify-start">
            <div className="w-[85%] rounded-xl p-3 text-white shadow-md"
              style={{ background: "linear-gradient(140deg,#065f46,#052e22)" }}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold">Aarogya Assist</span>
                <span className="text-[8px] text-emerald-300">GOLD PLAN</span>
              </div>
              <div className="card-chip w-7 h-5 mt-3" />
              <p className="mt-2 text-[8px] text-emerald-200/70">MEMBER ID</p>
              <p className="font-mono text-xs tracking-wider">IH-2026-48213</p>
              <p className="mt-1 text-[10px] font-semibold">Rahul Sharma</p>
            </div>
          </div>
          <div className="flex justify-end">
            <div className="bg-emerald-900 text-white text-xs px-3 py-2 rounded-2xl rounded-br-sm max-w-[80%]">
              Is maternity covered?
            </div>
          </div>
          <div className="flex justify-start">
            <div className="bg-white text-zinc-800 text-xs px-3 py-2 rounded-2xl rounded-bl-sm max-w-[80%] shadow-sm">
              Yes ✅ up to ₹50,000 after 9-month waiting.
            </div>
          </div>
        </div>
        {/* input */}
        <div className="bg-[#EFECE4] px-3 py-2.5 flex items-center gap-2">
          <div className="flex-1 bg-white rounded-full px-3 py-2 text-[11px] text-zinc-400">Type a message…</div>
          <div className="w-8 h-8 rounded-full bg-emerald-900 flex items-center justify-center">
            <PaperPlaneRight size={14} weight="fill" className="text-white" />
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function ChatbotInfographic() {
  const left = FEATURES.filter((f) => f.side === "left");
  const right = FEATURES.filter((f) => f.side === "right");
  return (
    <section id="infographic" className="max-w-6xl mx-auto px-6 py-20">
      <div className="text-center max-w-2xl mx-auto">
        <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 text-emerald-800 px-3 py-1 text-xs font-semibold mb-4">
          <ChatCircleDots size={15} weight="fill" /> How the chatbot helps
        </div>
        <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-emerald-950">
          Everything an employee needs, in one chat.
        </h2>
        <p className="mt-4 text-zinc-600">
          The Aarogya Assist Chatbot turns complex policy documents into instant, friendly answers.
        </p>
      </div>

      {/* Infographic: features around phone */}
      <div className="mt-14 grid lg:grid-cols-[1fr_auto_1fr] gap-6 lg:gap-10 items-center">
        <div className="space-y-4 order-2 lg:order-1">
          {left.map((f, i) => <FeaturePill key={f.title} f={f} i={i} align="left" />)}
        </div>
        <div className="order-1 lg:order-2"><PhoneMock /></div>
        <div className="space-y-4 order-3">
          {right.map((f, i) => <FeaturePill key={f.title} f={f} i={i} align="right" />)}
        </div>
      </div>

      {/* How it works flow */}
      <div className="mt-20">
        <p className="text-center text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-8">
          Four simple steps
        </p>
        <div className="relative grid md:grid-cols-4 gap-6">
          <div className="hidden md:block absolute top-7 left-[12%] right-[12%] h-0.5 bg-gradient-to-r from-emerald-200 via-emerald-300 to-emerald-200" />
          {STEPS.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              data-testid={`infographic-step-${i}`}
              className="relative text-center"
            >
              <div className="mx-auto w-14 h-14 rounded-full bg-emerald-900 text-white font-heading font-semibold text-lg flex items-center justify-center relative z-10 shadow-lg shadow-emerald-900/20">
                {s.n}
              </div>
              <h4 className="mt-4 font-heading font-semibold text-emerald-950">{s.t}</h4>
              <p className="mt-1 text-sm text-zinc-500 leading-relaxed px-2">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
